import axios from 'axios';

import UploadService from './UploadService';

const SectionService = {
  async getSections(meetingId) {
    if (!meetingId) {
      return null;
    }

    const response = await axios.get('/sections', {
      params: {
        meetingId,
      },
    });
    return response.data;
  },

  async uploadAudioImage({ image, sectionId }) {
    const form = new FormData();
    form.append('file', image);

    const response = await axios.post(
      `/sections/${sectionId}/audio-image`,
      form
    );
    return response.data;
  },

  async createSection({
    title,
    meetingId,
    file,
    question,
    image,
    text,
    setUploadProgress,
    prevSectionId,
  }) {
    try {
      let data;

      const lessThan5MB = file && file.size < 5 * 1024 * 1024;

      if (file) {
        let s3FileKey;
        if (lessThan5MB) {
          s3FileKey = await UploadService.uploadVideoToS3(
            file,
            setUploadProgress
          );
        } else {
          s3FileKey = await UploadService.uploadVideoToS3InParts(
            file,
            setUploadProgress
          );
        }

        data = {
          meetingId,
          s3FileKey,
          fileType: file.type.includes('video') ? 'video' : 'audio',
          title,
          prevSectionId,
        };
      } else if (question) {
        data = { question, meetingId, title, prevSectionId };
      } else if (text) {
        data = { meetingId, title, text, prevSectionId };
      }

      const response = await axios.post(`/sections`, data);

      if (image) {
        const form = new FormData();
        form.append('file', image);
        return SectionService.uploadAudioImage({
          image,
          sectionId: response.data.id,
        });
      }
      return response.data;
    } catch (error) {
      console.error('Error creating section:', error);
      throw error; // Re-throw the error to handle it in the calling function
    }
  },

  async createAttachmentSections({
    title,
    meetingId,
    files,
    text,
    setUploadProgress,
    prevSectionId,
    type,
    descriptions,
  }) {
    const data = new FormData();
    data.append('meetingId', meetingId);
    data.append('title', title);
    data.append('prevSectionId', prevSectionId);
    data.append('type', type);
    data.append('attachmentDescriptions', JSON.stringify(descriptions || []));

    if (text) {
      data.append('text', text);
    }

    files.forEach((file) => {
      data.append('files', file, file.name);
    });

    const response = await axios.post(`/sections/attachments`, data, {
      onUploadProgress: (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        );

        if (setUploadProgress) {
          setUploadProgress(progress);
        }
      },
    });

    return response.data;
  },

  async deleteSection({ sectionId }) {
    const response = await axios.delete(`/sections/${sectionId}`);
    return response.data;
  },

  async updateSection({ sectionId, order, question, note, title, text, trimSettings }) {
    const response = await axios.patch(`/sections/${sectionId}`, {
      order,
      question,
      note,
      title,
      text,
      trimSettings,
    });
    return response.data;
  },

  async updateSectionOrders({ meetingId, orders }) {
    const response = await axios.post('/sections/batch/order', {
      meetingId,
      orders,
    });
    return response.data;
  },

  async updateSectionsBatch({ meetingId, items }) {
    const response = await axios.post('/sections/batch/update', {
      meetingId,
      items,
    });
    return response.data;
  },

  async deleteSectionsBatch({ meetingId, ids }) {
    const response = await axios.post('/sections/batch/delete', {
      meetingId,
      ids,
    });
    return response.data;
  },
  async saveSection(id) {
    const response = await axios.post(`/sections/${id}/save`);
    return response.data;
  },

  async toggleSectionDeleteStatus(sectionId) {
    const response = await axios.post(`/sections/${sectionId}/delete`);
    return response.data;
  },

  async deleteSectionAttachment({ sectionId, attachmentId }) {
    const response = await axios.delete(
      `/sections/${sectionId}/attachments/${attachmentId}`
    );
    return response.data;
  },

  async updateSectionAttachment({ sectionId, attachment }) {
    const response = await axios.patch(
      `/sections/${sectionId}/attachments/${attachment.id}`,
      {
        name: attachment.name,
        description: attachment.description || '',
      }
    );
    return response.data;
  },

  async addSectionAttachment({
    sectionId,
    file,
    order,
    type,
    name,
    description,
  }) {
    const form = new FormData();
    form.append('file', file);
    form.append('order', order);
    form.append('type', type);
    form.append('name', name);
    form.append('description', description || '');

    const response = await axios.post(
      `/sections/${sectionId}/attachments`,
      form
    );
    return response.data;
  },

  async getSectionFrames(sectionId) {
    const response = await axios.get(`/sections/${sectionId}/frames`);

    return response.data;
  },

  async trimSectionVideo({ sectionId, startTime, endTime }) {
    const response = await axios.post(`/sections/${sectionId}/video/trim`, {
      startTime,
      endTime,
    });

    return response.data;
  },

  async copySectionFromLibrary({
    sourceSectionId,
    meetingId,
    title,
    trimStart = 0,
    trimEnd = 0,
    prevSectionId,
  }) {
    const payload = {
      sourceSectionId,
      meetingId,
      trimStart,
      trimEnd,
    };

    // Only include optional fields if they have values
    if (title) {
      payload.title = title;
    }
    if (prevSectionId) {
      payload.prevSectionId = prevSectionId;
    }

    const response = await axios.post('/sections/copy-from-library', payload);

    return response.data;
  },

  async createPlaceholderSection({ meetingId, title, type = 'video' }) {
    const response = await axios.post('/sections/placeholder', {
      meetingId,
      title,
      type,
    });
    return response.data;
  },

  async completeSection({ sectionId, s3FileKey, duration, fileType }) {
    const response = await axios.patch(`/sections/${sectionId}/complete`, {
      s3FileKey,
      duration,
      fileType,
    });
    return response.data;
  },

  async getUploadStatus(meetingId) {
    const response = await axios.get('/sections/upload-status', {
      params: { meetingId },
    });
    return response.data;
  },
};

export default SectionService;
